import { useState, useEffect, useCallback } from 'react';
import { getCtVarianceAlerts, getCtVariancePartner, getCtVarianceSettings, updateCtVarianceSettings } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  AlertTriangle, ShieldAlert, CheckCircle, Loader2, ChevronDown, ChevronUp,
  Settings, MapPin, Package, TrendingDown, TrendingUp, Eye, ArrowRight, X
} from 'lucide-react';

const SEVERITY_CONFIG = {
  critical: { label: 'Critical', color: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400 border-rose-200', icon: ShieldAlert },
  warning: { label: 'Warning', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 border-amber-200', icon: AlertTriangle },
  normal: { label: 'Normal', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 border-emerald-200', icon: CheckCircle },
};

const REASON_LABELS = {
  negative_stock: 'Negative stock count',
  stock_exceeds_expected: 'Stock exceeds expected (more than delivered)',
  high_sold_ratio: 'Unusually high sold ratio',
  no_stock: 'No stock data',
  within_threshold: 'Within threshold',
};

function SeverityBadge({ severity }) {
  const cfg = SEVERITY_CONFIG[severity] || SEVERITY_CONFIG.normal;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${cfg.color}`} data-testid={`variance-severity-${severity}`}>
      <Icon className="w-3 h-3" /> {cfg.label}
    </span>
  );
}

function PartnerDetailDialog({ open, onClose, apteka, month }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !apteka) return;
    setLoading(true);
    getCtVariancePartner(apteka.apteka_id, month)
      .then(res => setData(res.data))
      .catch(() => toast.error('Failed to load partner variance'))
      .finally(() => setLoading(false));
  }, [open, apteka, month]);

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col" data-testid="variance-partner-dialog">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            <Package className="w-4 h-4" />
            {apteka?.name} — Stock Variance Detail
          </DialogTitle>
          <DialogDescription className="text-xs">
            Product-level breakdown for {month}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        ) : data ? (
          <div className="flex-1 overflow-auto">
            {/* Summary */}
            <div className="grid grid-cols-4 gap-2 mb-4">
              <div className="rounded-lg bg-muted/50 p-2 text-center">
                <div className="text-[10px] text-muted-foreground">Expected</div>
                <div className="text-sm font-bold tabular-nums">{data.total_expected}</div>
              </div>
              <div className="rounded-lg bg-muted/50 p-2 text-center">
                <div className="text-[10px] text-muted-foreground">Actual</div>
                <div className="text-sm font-bold tabular-nums">{data.total_actual}</div>
              </div>
              <div className="rounded-lg bg-muted/50 p-2 text-center">
                <div className="text-[10px] text-muted-foreground">Sold</div>
                <div className="text-sm font-bold tabular-nums">{data.total_sold}</div>
              </div>
              <div className="rounded-lg bg-muted/50 p-2 text-center">
                <div className="text-[10px] text-muted-foreground">Sold %</div>
                <div className={`text-sm font-bold tabular-nums ${data.overall_sold_pct > 90 ? 'text-rose-600' : data.overall_sold_pct > 70 ? 'text-amber-600' : 'text-emerald-600'}`}>{data.overall_sold_pct}%</div>
              </div>
            </div>

            {/* Alert counts */}
            {(data.critical_count > 0 || data.warning_count > 0) && (
              <div className="flex gap-2 mb-3">
                {data.critical_count > 0 && <Badge variant="destructive" className="text-[10px]">{data.critical_count} critical</Badge>}
                {data.warning_count > 0 && <Badge className="bg-amber-500 text-white text-[10px]">{data.warning_count} warning</Badge>}
                <Badge variant="outline" className="text-[10px]">{data.total_products} total products</Badge>
              </div>
            )}

            {/* Product table */}
            <div className="table-responsive">
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="border-b bg-muted/30">
                    <th className="text-left px-2 py-1.5 font-semibold">Product</th>
                    <th className="text-right px-2 py-1.5 font-semibold">Open</th>
                    <th className="text-right px-2 py-1.5 font-semibold">Del</th>
                    <th className="text-right px-2 py-1.5 font-semibold">T.In</th>
                    <th className="text-right px-2 py-1.5 font-semibold">T.Out</th>
                    <th className="text-right px-2 py-1.5 font-semibold">Expected</th>
                    <th className="text-right px-2 py-1.5 font-semibold">Actual</th>
                    <th className="text-right px-2 py-1.5 font-semibold">Sold</th>
                    <th className="text-right px-2 py-1.5 font-semibold">%</th>
                    <th className="text-center px-2 py-1.5 font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.products.map(p => (
                    <tr key={p.product_id} className={`border-b hover:bg-muted/20 ${p.severity === 'critical' ? 'bg-rose-50/50 dark:bg-rose-950/10' : p.severity === 'warning' ? 'bg-amber-50/50 dark:bg-amber-950/10' : ''}`}>
                      <td className="px-2 py-1.5 font-medium max-w-[140px] truncate">{p.product_name}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{p.old_ct}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{p.delivered_1 + p.delivered_2 + p.delivered_3 + p.delivered_4}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums text-emerald-600">{p.transfer_in || '-'}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums text-rose-600">{p.transfer_out || '-'}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums font-semibold">{p.expected}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums font-semibold">{p.actual}</td>
                      <td className={`px-2 py-1.5 text-right tabular-nums font-bold ${p.sold < 0 ? 'text-rose-600' : ''}`}>{p.sold}</td>
                      <td className={`px-2 py-1.5 text-right tabular-nums ${p.sold_pct > 90 ? 'text-rose-600 font-bold' : ''}`}>{p.sold_pct}%</td>
                      <td className="px-2 py-1.5 text-center"><SeverityBadge severity={p.severity} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-sm text-muted-foreground">No data available</div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function CtVariancePanel({ month, state, district }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedDistrict, setExpandedDistrict] = useState(null);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState({ high_sold_pct: 90, min_units_for_alert: 5 });

  const load = useCallback(async () => {
    if (!month || !state || state === 'all') return;
    setLoading(true);
    try {
      const [alertRes, settingsRes] = await Promise.all([
        getCtVarianceAlerts(month, state, district),
        getCtVarianceSettings()
      ]);
      setData(alertRes.data);
      setSettings(settingsRes.data);
    } catch {
      toast.error('Failed to load variance alerts');
    } finally {
      setLoading(false);
    }
  }, [month, state, district]);

  useEffect(() => { load(); }, [load]);

  const handleSaveSettings = async () => {
    try {
      await updateCtVarianceSettings(settings);
      toast.success('Variance settings saved');
      setShowSettings(false);
      await load();
    } catch {
      toast.error('Failed to save settings');
    }
  };

  if (loading && !data) {
    return (
      <div className="rounded-xl border bg-card p-4" data-testid="variance-panel-loading">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="text-sm">Analyzing stock variances...</span>
        </div>
      </div>
    );
  }

  if (!data || data.total_partners === 0) return null;

  const hasAlerts = data.partners_with_alerts > 0;
  const hasCritical = data.critical_count > 0;
  const borderColor = hasCritical ? 'border-rose-300 dark:border-rose-700' : hasAlerts ? 'border-amber-300 dark:border-amber-700' : 'border-emerald-300 dark:border-emerald-800';
  const headerBg = hasCritical ? 'bg-rose-50 dark:bg-rose-950/20' : hasAlerts ? 'bg-amber-50 dark:bg-amber-950/20' : 'bg-emerald-50 dark:bg-emerald-950/20';

  // Get partners for expanded district
  const districtAlerts = expandedDistrict
    ? (data.alerts || []).filter(a => a.district === expandedDistrict)
    : [];

  return (
    <div className={`rounded-xl border-2 overflow-hidden transition-all ${borderColor}`} data-testid="ct-variance-panel">
      {/* HEADER */}
      <div className={`px-4 py-3 ${headerBg}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {hasCritical ? <ShieldAlert className="w-5 h-5 text-rose-600 animate-pulse" /> : hasAlerts ? <AlertTriangle className="w-5 h-5 text-amber-600" /> : <CheckCircle className="w-5 h-5 text-emerald-600" />}
            <div>
              <h3 className="text-sm font-bold flex items-center gap-2" data-testid="variance-title">
                Stock Variance Alerts — {month}
                {hasCritical && <Badge variant="destructive" className="text-[9px] h-4 animate-pulse">{data.critical_count} Critical</Badge>}
                {data.warning_count > 0 && <Badge className="bg-amber-500 text-white text-[9px] h-4">{data.warning_count} Warning</Badge>}
                {!hasAlerts && <Badge className="bg-emerald-600 text-white text-[9px] h-4">All Clear</Badge>}
              </h3>
              <p className="text-[10px] text-muted-foreground mt-0.5">
                {data.partners_with_alerts}/{data.total_partners} partners flagged
                {data.critical_count > 0 && <span className="ml-1 text-rose-600 font-bold"> | {data.critical_count} critical</span>}
                {data.warning_count > 0 && <span className="ml-1 text-amber-600 font-bold"> | {data.warning_count} warning</span>}
              </p>
            </div>
          </div>
          <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => setShowSettings(true)} data-testid="variance-settings-btn">
            <Settings className="w-3.5 h-3.5" />
          </Button>
        </div>

        {/* Alert ratio bar */}
        {data.total_partners > 0 && (
          <div className="mt-2.5 flex items-center gap-3">
            <div className="flex-1">
              <Progress value={hasAlerts ? Math.round((data.partners_with_alerts / data.total_partners) * 100) : 0} className="h-2.5" />
            </div>
            <span className={`text-sm font-bold tabular-nums ${hasCritical ? 'text-rose-600' : hasAlerts ? 'text-amber-600' : 'text-emerald-600'}`} data-testid="variance-alert-pct">
              {hasAlerts ? Math.round((data.partners_with_alerts / data.total_partners) * 100) : 0}% flagged
            </span>
          </div>
        )}
      </div>

      {/* CRITICAL ALERT BANNER */}
      {hasCritical && (
        <div className="px-4 py-2.5 bg-rose-100/80 dark:bg-rose-950/30 border-b border-rose-300 dark:border-rose-800 flex items-start gap-2" data-testid="variance-critical-banner">
          <ShieldAlert className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-xs font-bold text-rose-800 dark:text-rose-300">Critical variance detected!</p>
            <p className="text-[10px] text-rose-700 dark:text-rose-400 mt-0.5">
              {data.critical_count} partner(s) have impossible stock values (negative counts or stock exceeding deliveries).
              Review immediately — possible data entry errors or discrepancies.
            </p>
          </div>
        </div>
      )}

      {/* DISTRICT GRID */}
      {hasAlerts && (
        <div className="px-4 py-3 bg-card" data-testid="variance-district-section">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-semibold text-muted-foreground flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5" /> District Variance Summary
            </span>
            <span className="text-[10px] text-muted-foreground">{data.district_summary?.length} districts</span>
          </div>

          <div className="space-y-1.5" data-testid="variance-district-list">
            {data.district_summary?.map(d => {
              const isExpanded = expandedDistrict === d.district;
              const hasDCritical = d.critical > 0;
              const hasDWarning = d.warning > 0;
              const bgClass = hasDCritical ? 'border-rose-200 dark:border-rose-800 bg-rose-50/50 dark:bg-rose-950/10' : hasDWarning ? 'border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-950/10' : 'border-emerald-200 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-950/10';

              return (
                <div key={d.district} className="rounded-lg border overflow-hidden" data-testid={`variance-district-${d.district}`}>
                  <button
                    onClick={() => setExpandedDistrict(isExpanded ? null : d.district)}
                    className={`w-full px-3 py-2.5 flex items-center gap-3 text-left transition-colors hover:bg-muted/30 ${bgClass}`}
                    data-testid={`variance-district-row-${d.district}`}
                  >
                    <div className="min-w-[120px] shrink-0">
                      <span className="text-xs font-bold">{d.district}</span>
                    </div>
                    <div className="flex items-center gap-2 flex-1">
                      {d.critical > 0 && (
                        <Badge variant="destructive" className="text-[8px] h-4">{d.critical} critical</Badge>
                      )}
                      {d.warning > 0 && (
                        <Badge className="bg-amber-500 text-white text-[8px] h-4">{d.warning} warning</Badge>
                      )}
                      {d.normal > 0 && (
                        <span className="text-[10px] text-muted-foreground">{d.normal} OK</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-[10px] text-muted-foreground tabular-nums">{d.total} partners</span>
                      {isExpanded ? <ChevronUp className="w-3.5 h-3.5 text-muted-foreground" /> : <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />}
                    </div>
                  </button>

                  {/* Expanded partner alerts */}
                  {isExpanded && (
                    <div className="border-t bg-card" data-testid={`variance-district-expanded-${d.district}`}>
                      {districtAlerts.length > 0 ? (
                        <div className="max-h-60 overflow-y-auto divide-y">
                          {districtAlerts.map(alert => (
                            <div key={alert.apteka_id} className="flex items-center justify-between px-3 py-2 hover:bg-muted/20" data-testid={`variance-partner-${alert.apteka_id}`}>
                              <div className="flex items-center gap-2 min-w-0">
                                <SeverityBadge severity={alert.severity} />
                                <span className="text-xs font-medium truncate">{alert.name}</span>
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                {alert.critical_count > 0 && <span className="text-[10px] text-rose-600 font-bold tabular-nums">{alert.critical_count}C</span>}
                                {alert.warning_count > 0 && <span className="text-[10px] text-amber-600 font-bold tabular-nums">{alert.warning_count}W</span>}
                                <span className="text-[10px] text-muted-foreground tabular-nums">/{alert.total_products}</span>
                                <Button
                                  size="sm" variant="ghost"
                                  className="h-5 text-[9px] gap-0.5 px-1.5"
                                  onClick={() => setSelectedPartner(alert)}
                                  data-testid={`variance-view-${alert.apteka_id}`}
                                >
                                  <Eye className="w-2.5 h-2.5" /> Detail
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-4 text-xs text-muted-foreground">No alerts in this district</div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* No alerts state */}
      {!hasAlerts && (
        <div className="px-4 py-6 text-center bg-card" data-testid="variance-no-alerts">
          <CheckCircle className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
          <p className="text-sm font-medium text-emerald-700 dark:text-emerald-400">All stock counts are within expected range</p>
          <p className="text-[10px] text-muted-foreground mt-1">{data.total_partners} partners checked, no variance issues detected</p>
        </div>
      )}

      {/* Partner Detail Dialog */}
      <PartnerDetailDialog
        open={!!selectedPartner}
        onClose={() => setSelectedPartner(null)}
        apteka={selectedPartner}
        month={month}
      />

      {/* Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="max-w-sm" data-testid="variance-settings-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">Variance Alert Settings</DialogTitle>
            <DialogDescription className="text-xs">Configure when stock variance alerts are triggered</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs font-medium">High Sold % Threshold</label>
              <Input
                type="number" min={50} max={100}
                value={settings.high_sold_pct}
                onChange={e => setSettings(prev => ({ ...prev, high_sold_pct: parseInt(e.target.value) || 90 }))}
                className="h-8 text-xs mt-1"
                data-testid="variance-threshold-input"
              />
              <p className="text-[10px] text-muted-foreground mt-1">
                Alert when sold percentage exceeds {settings.high_sold_pct}% of expected stock.
              </p>
            </div>
            <div>
              <label className="text-xs font-medium">Minimum Units for Alert</label>
              <Input
                type="number" min={1} max={100}
                value={settings.min_units_for_alert}
                onChange={e => setSettings(prev => ({ ...prev, min_units_for_alert: parseInt(e.target.value) || 5 }))}
                className="h-8 text-xs mt-1"
                data-testid="variance-min-units-input"
              />
              <p className="text-[10px] text-muted-foreground mt-1">
                Ignore warnings for products with expected stock below {settings.min_units_for_alert} units.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setShowSettings(false)}>Cancel</Button>
            <Button size="sm" onClick={handleSaveSettings} data-testid="variance-save-settings-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
